import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '../models';
import { SYSTEM_ERROR_ROOT, SYSTEM_ERROR_TYPE } from './shared-data';

export const ErrorLocTypeGuard = (value: string): value is ErrorLoc => {
	return Object.prototype.hasOwnProperty.call(errorLocs, value);
};

const sharedProperties = {
	htmlAllowed: false,
	shared: true,
};

type BaseErrorLoc =
	| '949807cca5a84dfe85c9456cc45548b4'
	| 'CMSystemErrorDefault'
	| 'CMSystemErrorAuth'
	| 'CMSystemErrorFormIssue'
	| 'CMSystemErrorTryAgain'
	| 'CMSystemErrorNoChanges'
	| '4713a092237041b6ba861ff284a95a00'
	| 'e7225c2003514b9bbdbad677f0effd52'
	| '85043b8efb5f41f088377b285ca15ca3'
	| 'd25aaeba218f49b2afe95f07b610db0e'
	| '61d3241faf5717065790ca4883186f35'
	| '3c1eba257e7442b684d015e37eac3340'
	| 'SPMergeUniqueLoser'
	| '2e6da1750f074bb3b618f885aa1706e8'
	| 'd8b4b123-c8f4-4c3b-a912-6e6a4d8f0e5a'
	| '43c4bcae3869439e80d628e08482bcf3'
	| 'CMSystemErrorCsvUploadEmptyFile';

const LOCS: LocDefs<BaseErrorLoc> = {
	'949807cca5a84dfe85c9456cc45548b4': {
		defaultValue: 'Password cannot be the same as previous password.',
		description:
			'The error text that is returned from the api when a candidate attempts to change their password to what it is currently set to.',
		displayPath: `System|Error|Password Previously Used`,
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.CandidateAccessLogin,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
		legacyName: 'Core/Error.GUID.949807cca5a84dfe85c9456cc45548b4',
	},
	CMSystemErrorDefault: {
		defaultValue: 'A system error has occurred',
		displayPath: `${SYSTEM_ERROR_ROOT}|System Error`,
		description: 'A generic message for when there is an unknown system error',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Core,
		portal: 'All',
		...sharedProperties,
	},
	CMSystemErrorAuth: {
		defaultValue: 'You need to log in again',
		displayPath: `${SYSTEM_ERROR_ROOT}|Unauthorized`,
		description:
			'Error shown when user is redirected to login due to a problem with their current session.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Core,
		htmlAllowed: false,
		shared: true,
		portal: 'All',
	},
	CMSystemErrorFormIssue: {
		defaultValue: 'Please check all form fields for errors and try again',
		displayPath: `${SYSTEM_ERROR_ROOT}|Form Error`,
		description: 'A generic message for when something in a form needs addressed',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Core,
		htmlAllowed: false,
		shared: true,
		portal: 'All',
	},
	CMSystemErrorTryAgain: {
		defaultValue: 'An error occurred. Please wait a moment, refresh your screen, and try again',
		displayPath: `${SYSTEM_ERROR_ROOT}|Wait Error`,
		description: 'A generic message for when the user/candidate needs to wait and try again',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Core,
		htmlAllowed: false,
		shared: true,
		portal: 'All',
	},
	CMSystemErrorNoChanges: {
		defaultValue: 'No changes made',
		displayPath: `${SYSTEM_ERROR_ROOT}|No Change Error`,
		description:
			'Notification message saying that no changes were detected while updating an item or feature',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Core,
		htmlAllowed: false,
		shared: true,
		portal: 'All',
	},
	'4713a092237041b6ba861ff284a95a00': {
		defaultValue: 'Domain names must be unique',
		displayPath: `System Settings|Tagging|Domain Exists Error`,
		description: 'Attempting to create a new domain with a name of an existing domain',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Tagging,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	e7225c2003514b9bbdbad677f0effd52: {
		defaultValue:
			'An account at LineUp cannot be created because you do not have a unique email',
		displayPath: `System|Error|Duplicate Email for Lineup`,
		description:
			'Notification message saying that the lineup opt-in could not be created due to duplicate emails.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.BenefitLineup,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	'85043b8efb5f41f088377b285ca15ca3': {
		defaultValue: 'Postal Code must be less than 16 characters.',
		displayPath: `System|Error|Invalid Postal Code Length`,
		description: 'Notification message saying that the postal code is invalid.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Core,
		htmlAllowed: false,
		shared: false,
		portal: 'CP',
	},
	d25aaeba218f49b2afe95f07b610db0e: {
		defaultValue: 'Invalid login',
		displayPath: `System Settings|Login|Invalid login`,
		description: 'Error shown when invalid credentials are used during a login.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Core,
		htmlAllowed: false,
		shared: true,
		portal: 'All',
		legacyName: 'Core/Error.GUID.d25aaeba218f49b2afe95f07b610db0e',
	},
	'61d3241faf5717065790ca4883186f35': {
		defaultValue: 'This operation is not permitted for candidates that are employees of Dell',
		displayPath: `System|Error|Invalid Affiliation Type`,
		description:
			'Error shown when other achievement removal fails becuase of the candidate affiliation.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.CandidateAccessLoginEmcOtherAchievements,
		htmlAllowed: false,
		shared: true,
		portal: 'All',
	},
	SPMergeUniqueLoser: {
		defaultValue: 'Requested candidate to merge into is unique.',
		displayPath: `System|Error|Unique Loser`,
		description: 'Error displayed when requested candidate to merge into is unique.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.MergeCandidates,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	'3c1eba257e7442b684d015e37eac3340': {
		defaultValue:
			'The value supplied for the Affiliate Id does not match the required criterion.',
		displayPath: `System|Error|Invalid Affiliate Id format`,
		description:
			'Error displayed when an invalid affiliate id format is supplied with the affiliation.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
		htmlAllowed: false,
		shared: false,
		portal: 'All',
	},
	'2e6da1750f074bb3b618f885aa1706e8': {
		defaultValue: 'No Vouchers available',
		displayPath: `System|Error|No Vouchers Available`,
		description: 'Error displayed when no benefit vouchers are available.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.BenefitLineup,
		htmlAllowed: false,
		shared: false,
		portal: 'SP',
	},
	'd8b4b123-c8f4-4c3b-a912-6e6a4d8f0e5a': {
		defaultValue: 'Exam Authorization Id is invalid.',
		displayPath: `System|Error|Exam Authorization Invalid Error`,
		description:
			'Error displayed when an invalid exam authorization id is provided for an exam creation or update.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Exam,
		htmlAllowed: false,
		shared: false,
		portal: 'All',
	},
	'43c4bcae3869439e80d628e08482bcf3': {
		defaultValue: 'Exam Authorization Id is invalid.',
		displayPath: `System|Error|Exam Authorization Mismatch Error`,
		description:
			'Error displayed when an invalid exam authorization id is provided for an exam creation or update.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Exam,
		htmlAllowed: false,
		shared: false,
		portal: 'All',
	},
	CMSystemErrorCsvUploadEmptyFile: {
		defaultValue: 'File is empty.',
		displayPath: `System|Error|Csv Upload Empty File`,
		description: 'Error displayed when there is no data in the file.',
		displayType: SYSTEM_ERROR_TYPE,
		feature: HubFeature.Core,
		htmlAllowed: false,
		shared: false,
		portal: 'All',
	},
};

export type ErrorLoc = BaseErrorLoc | 'ba5e95f44eff426baed79f1230c977a8';

export const errorLocs: LocDefs<ErrorLoc> = {
	...LOCS,
	ba5e95f44eff426baed79f1230c977a8: LOCS.CMSystemErrorDefault,
};

export const GLOBAL_ERROR_KEYS = MapLocKeys<ErrorLoc>(errorLocs);
export type ErrorLocType = LocMap<ErrorLoc>;
